// React imports
import React from 'react';

// Third-party imports
import { useMediaQuery } from '@material-ui/core';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

// Types
import { Theme } from '@material-ui/core/styles';
import { ApplicationType, SaveConfiguredDiscount } from '@spike/discount-model';

// UI Components
import { ConfirmDialog, Spinner, OverFullWindow } from 'components/UI';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import DescriptionText from 'components/UI/V3/DescriptionText';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import { SelectionBar } from 'components/UI/V3/SelectionBar';
import SuccessScreen from 'components/UI/V3/SuccessScreen';

// Local Components
import { AddDiscount } from 'components/Discounts/AddDiscount/AddDiscount';
import { DiscountsProvider, useDiscounts } from './DiscountsProvider';
import { DiscountsTable } from './List/DiscountsTable/DiscountsTable';
import { Discount } from './List/types';
import { getApplyToLabel, getBusinessAreaCounts, formatBusinessAreaLabels } from './Utils';

// Styles
import styles from './DiscountsComponent.module.scss';

export const DiscountsComponent: React.FC = () => {
    return (
        <DiscountsProvider>
            <DiscountsContent />
        </DiscountsProvider>
    );
};

const DiscountsContent: React.FC = () => {
    const {
        discounts: configuredDiscounts,
        isFirstRender,
        searchText,
        searchTextChangeHandler,
        selectedFilter,
        filterChangeHandler,
        showAddDiscount,
        editDiscount,
        openAddForm,
        openEditForm,
        openViewForm,
        closeForm,
        activateDiscount,
        deactivateDiscount,
        deleteDiscount,
        resourcesCount
    } = useDiscounts();

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const [successDiscount, setSuccessDiscount] = React.useState<SaveConfiguredDiscount | null>(null);

    const [showConfirm, setShowConfirm] = React.useState<{
        show: boolean;
        action: 'Activate' | 'Deactivate';
        discountId: number | null;
    }>({
        show: false,
        action: 'Activate',
        discountId: null
    });

    // Map ConfiguredDiscount to local Discount format for the table
    const discounts: Discount[] = React.useMemo(() => {
        return configuredDiscounts.map(discount => {
            const businessAreaCounts = getBusinessAreaCounts(discount);
            const formattedLabels = formatBusinessAreaLabels(businessAreaCounts, resourcesCount);

            // Merge displayLabel into businessAreaCounts
            const businessAreaCountsWithLabels = businessAreaCounts.map(item => {
                const formatted = formattedLabels.find(f => f.businessArea === item.businessArea);
                return {
                    ...item,
                    displayLabel: formatted?.displayLabel || `${item.label} (${item.count})`
                };
            });

            return {
                id: discount.id,
                name: discount.name || '',
                type: discount.applicationType === ApplicationType.CUSTOMER_TAG ? 'Client Tag' : 'Multi Pet Discount',
                services: `${discount.items.length} item(s)`,
                businessAreaCounts: businessAreaCountsWithLabels,
                applyTo: getApplyToLabel(discount.values || {}),
                used: discount.usage,
                isActive: discount.active
            };
        });
    }, [configuredDiscounts, resourcesCount]);

    const handleToggleStatus = (id: number) => {
        const discount = configuredDiscounts.find(d => d.id === id);
        if (discount) {
            setShowConfirm({
                show: true,
                action: discount.active ? 'Deactivate' : 'Activate',
                discountId: id
            });
        }
    };

    const handleConfirmToggle = async () => {
        if (showConfirm.discountId !== null) {
            const discount = configuredDiscounts.find(d => d.id === showConfirm.discountId);
            if (discount) {
                try {
                    if (showConfirm.action === 'Activate') {
                        await activateDiscount(discount);
                    } else {
                        await deactivateDiscount(discount);
                    }
                } catch (error) {
                    console.error('Error toggling discount status:', error);
                }
            }
            setShowConfirm({ show: false, action: 'Activate', discountId: null });
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await deleteDiscount(id);
        } catch (error) {
            console.error('Error deleting discount:', error);
        }
    };

    const handleView = (id: number) => {
        const discount = configuredDiscounts.find(d => d.id === id);
        if (discount) {
            openViewForm(discount);
        }
    };

    const filterOptions = [
        { id: 'active', name: 'Active' },
        { id: 'inactive', name: 'Inactive' },
        { id: 'all', name: 'All' }
    ];

    const handleSelect = (id: string) => {
        filterChangeHandler(id);
    };

    const handleEditDiscount = (id: number) => {
        const discount = configuredDiscounts.find(d => d.id === id);
        if (discount) {
            openEditForm(discount);
        }
    };

    const handleDiscountSuccess = (savedDiscount: SaveConfiguredDiscount) => {
        setSuccessDiscount(savedDiscount);
    };

    const handleSuccessClose = () => {
        setSuccessDiscount(null);
        closeForm();
    };

    const renderContent = () => {
        // Only show full-screen spinner for initial load
        if (isFirstRender) {
            return (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            );
        }

        if (successDiscount) {
            return (
                <OverFullWindow
                    showMobileLogo
                    title="Adding Discount"
                    onClose={handleSuccessClose}
                >
                    <SuccessScreen
                        onClick={handleSuccessClose}
                        title={'Good Job'}
                        message={
                            <SuccessMessage
                                discountName={successDiscount.name!}
                                isEditing={Boolean(editDiscount?.id)}
                            />
                        }
                        buttonLabel={'Go to Discounts'}
                    />
                </OverFullWindow>
            );
        }

        if (showAddDiscount || editDiscount) {
            return (
                <AddDiscount
                    onClose={closeForm}
                    onSuccess={handleDiscountSuccess}
                />
            );
        }

        return (
            <div className={styles.wrapperContainer}>
                <ConfirmDialog
                    open={showConfirm.show}
                    title={
                        <span>
                            {showConfirm.action === 'Activate'
                                ? 'Activate Automatic discount'
                                : 'Deactivate Automatic discount'}
                        </span>
                    }
                    question={
                        <span>
                            {showConfirm.action === 'Activate'
                                ? 'Are you sure you want to activate this automatic discount?'
                                : 'Are you sure you want to deactivate this automatic discount?'}
                        </span>
                    }
                    cancelButtonLabel="No"
                    confirmButtonLabel="Yes"
                    onCancel={() => setShowConfirm({ show: false, action: 'Activate', discountId: null })}
                    onConfirm={handleConfirmToggle}
                />
                <div className={styles.headWrapper}>
                    <DefaultHeader
                        title={'Automatic Discounts'}
                        buttonLabel={isMobile ? 'Add' : 'Add Discount'}
                        displayButton={true}
                        onClick={openAddForm}
                        id="discount_add_button"
                        className={styles.headerContainer}
                        buttonIcon={faPlus}
                    />
                    <DescriptionText
                        className={styles.label}
                        text="Set up custom automatic discounts to reward special clients or multi-pet families."
                    ></DescriptionText>

                    <SelectionBar
                        options={filterOptions}
                        selectedId={selectedFilter}
                        onSelect={handleSelect}
                        className={styles.selectionBar}
                    />

                    <SearchBar
                        searchText={searchText}
                        onChangeSearchText={searchTextChangeHandler}
                        placeholder="Search by discount name"
                        className={styles.searchBarContainer}
                    />
                </div>
                <div className={styles.fullWidthContainer}>
                    <DiscountsTable
                        discounts={discounts}
                        onEdit={handleEditDiscount}
                        onView={handleView}
                        onToggleStatus={handleToggleStatus}
                        onDelete={handleDelete}
                        className={styles.table}
                    />
                </div>
            </div>
        );
    };

    return <div className={styles.root}>{renderContent()}</div>;
};

const SuccessMessage = ({ discountName, isEditing }: { discountName: string; isEditing: boolean }) => {
    return (
        <div style={{ textAlign: 'center' }}>
            <span style={{ fontWeight: 'bold' }}>{discountName}</span>
            <span>{` was successfully ${isEditing ? 'edited.' : 'created.'}`}</span>
        </div>
    );
};
